import { useId } from "react";
import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { Content, Props, Wrapper } from "./Dropdown.Parts";

/**
 * A floating overlay that handles portal rendering and focus trapping
 * (`FloatingFocusManager`), but nothing else. Keyboard navigation, item
 * registration, and ARIA roles are all left to the caller via `useInteractions`.
 *
 * Use this when the content isn't a list of options — e.g. a form, an action
 * panel, or any case where you need custom navigation that `DropdownList`
 * doesn't support.
 *
 * For standard option menus, use `DropdownList` instead.
 */
export const Dropdown = ({
  id: idProp,
  context,
  refs,
  floatingStyles,
  getFloatingProps,
  children,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  initialFocus,
  onMouseDown,
  floating = true,
}: Props) => {
  const generatedId = useId();

  if (!context.open) return null;

  const id = idProp || generatedId;

  const MenuContent = (
    <Wrapper
      ref={refs.setFloating}
      style={floatingStyles}
      {...getFloatingProps({ ref: refs.setFloating, id: id })}
    >
      <Content
        /**
         * This ensures the content doesn't receive tab focus
         * There was a bug with Select components in radix modals, where the modal's focus trap
         * would redirect focus to this scrollable element during tab navigation.
         * This would prevent the Select from closing when focus left the component.
         */
        tabIndex={-1}
        $maxHeight={maxHeight}
        $maxWidth={maxWidth}
        $minHeight={minHeight}
        $minWidth={minWidth}
        onMouseDown={onMouseDown}
      >
        {children}
      </Content>
    </Wrapper>
  );

  if (floating)
    return (
      <FloatingPortal>
        <FloatingFocusManager
          context={context}
          initialFocus={initialFocus}
          modal={false}
        >
          {MenuContent}
        </FloatingFocusManager>
      </FloatingPortal>
    );

  return MenuContent;
};
